/**
 * HrOnboarding — the mission control for onboarding & offboarding journeys.
 *
 * Sections:
 *  • Summary tiles (in-flight, joined, at-risk, alumni)
 *  • In-flight journeys table (with quick actions)
 *  • Offboarding tab
 *  • Alumni tab
 *  • Settings tab (alumni portal toggle, default template)
 *
 * When "Confirm joined" is clicked, we fire canvas-confetti to celebrate.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import confetti from "canvas-confetti";
import { OffboardingDialog } from "./hr/OffboardingDialog";
import { BuddyProgramTab } from "./hr/BuddyProgramTab";
import { OnboardingTaskItem, AddCustomTaskButton, BulkAddTasksButton, ImportFromTemplateButton, SortableTaskList, BulkTaskToolbar, TaskListHeader } from "./hr/OnboardingTaskItem";
import {
  Rocket, PartyPopper, Users, LogOut, GraduationCap, Sparkles, Mail,
  ChevronRight, AlertTriangle, Settings2, ExternalLink, HeartHandshake, Coffee, Smile, Meh, Frown, Target, RefreshCw,
  ArrowUpDown, ArrowUp, ArrowDown, Wand2, Loader2, Trash2,
} from "lucide-react";

const STATUS_TONES = {
  pre_boarding: "bg-blue-500/10 text-blue-700 border-blue-500/30",
  active: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30",
  completed: "bg-slate-500/10 text-slate-700 border-slate-500/30",
  paused: "bg-amber-500/10 text-amber-700 border-amber-500/30",
  in_progress: "bg-amber-500/10 text-amber-700 border-amber-500/30",
};

export default function HrOnboarding() {
  const { activeWorkspaceId } = useAuth();
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState("onboarding");
  const [journeys, setJourneys] = useState([]);
  const [offJourneys, setOffJourneys] = useState([]);
  const [alumni, setAlumni] = useState([]);
  const [selJourneyId, setSelJourneyId] = useState(null);
  const [countdowns, setCountdowns] = useState([]);
  const [myCheckin, setMyCheckin] = useState(null);
  // R268iii · Admin-only cleanup wizard for duplicate onboarding journeys
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [cleanupBusy, setCleanupBusy] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);
  const [mergeByName, setMergeByName] = useState(false);
  const params = useParams();

  const loadAll = async () => {
    if (!activeWorkspaceId) return;
    try {
      const [s, j, o, a, c, mc] = await Promise.all([
        api.get(`/workspaces/${activeWorkspaceId}/onboarding/summary`),
        api.get(`/workspaces/${activeWorkspaceId}/onboarding/journeys`),
        api.get(`/workspaces/${activeWorkspaceId}/offboarding/journeys`),
        api.get(`/workspaces/${activeWorkspaceId}/alumni`),
        api.get(`/workspaces/${activeWorkspaceId}/onboarding/countdowns`),
        api.get(`/me/onboarding/checkins/pending`).catch(() => ({ data: [] })),
      ]);
      setSummary(s.data); setJourneys(j.data || []); setOffJourneys(o.data || []);
      setAlumni(a.data || []); setCountdowns(c.data || []);
      setMyCheckin((mc.data || [])[0] || null);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load onboarding data"));
    }
  };
  useEffect(() => { loadAll(); }, [activeWorkspaceId]);
  useEffect(() => { if (params.jid) setSelJourneyId(params.jid); }, [params.jid]);

  const openMagicLink = async (jid) => {
    try {
      const r = await api.post(`/onboarding/journeys/${jid}/magic-link`);
      // Copy to clipboard + open in new tab
      try { await navigator.clipboard.writeText(r.data.url); } catch (_) { /* clipboard unavailable — non-fatal */ }
      toast.success("Magic link copied — share it with the hire");
      window.open(r.data.url, "_blank", "noopener,noreferrer");
    } catch (e) { toast.error(apiErrorMessage(e, "Could not create link")); }
  };

  // Feb 2026 · Manager-facing "Resync HR journeys" trigger. Wraps
  // POST /workspaces/{ws}/onboarding/backfill: re-hydrates existing
  // journey snapshots and auto-starts offboarding journeys for anyone
  // whose exit_date was edited on the master without triggering the
  // journey. Confirmation gate + result toast so managers get feedback.
  const [resyncing, setResyncing] = useState(false);
  const resyncJourneys = async () => {
    if (!activeWorkspaceId || resyncing) return;
    if (!window.confirm(
      "Resync every onboarding + offboarding journey to the current employee master?\n\n" +
      "This will also auto-start offboarding for any employee who has an exit date but no active offboarding journey.")) return;
    setResyncing(true);
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/onboarding/backfill`);
      toast.success(
        `Resynced ${data.onboarding_snapshot_resynced} onboarding + ${data.offboarding_snapshot_resynced} offboarding · ` +
        `${data.offboarding_auto_started} new offboarding journey${data.offboarding_auto_started === 1 ? "" : "s"} started`);
      loadAll();
    } catch (e) { toast.error(apiErrorMessage(e, "Resync failed")); }
    finally { setResyncing(false); }
  };

  return (
    <div className="pb-8" data-testid="hr-onboarding-page">
      <PageHeader icon={Rocket} title="Onboarding & Offboarding" subtitle="Every hire deserves a WOW moment. Every exit deserves respect." />
      <div className="px-4 sm:px-6 md:px-10">
        <SummaryTiles s={summary} />
        {myCheckin && <PendingCheckinBanner checkin={myCheckin} onDone={loadAll} />}
        {countdowns.length > 0 && <CountdownStrip rows={countdowns} onOpen={(j) => setSelJourneyId(j.id)} onLink={(j) => openMagicLink(j.id)} />}
        <Tabs value={tab} onValueChange={setTab} className="mt-6">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <TabsList className="rounded-none flex w-full sm:w-auto overflow-x-auto">
              <TabsTrigger value="onboarding" className="rounded-none" data-testid="hr-tab-onboarding"><Sparkles className="w-3.5 h-3.5 mr-1.5" /> Onboarding</TabsTrigger>
              <TabsTrigger value="buddy" className="rounded-none" data-testid="hr-tab-buddy"><HeartHandshake className="w-3.5 h-3.5 mr-1.5" /> Buddy Program</TabsTrigger>
              <TabsTrigger value="offboarding" className="rounded-none" data-testid="hr-tab-offboarding"><LogOut className="w-3.5 h-3.5 mr-1.5" /> Offboarding</TabsTrigger>
              <TabsTrigger value="alumni" className="rounded-none" data-testid="hr-tab-alumni"><GraduationCap className="w-3.5 h-3.5 mr-1.5" /> Alumni</TabsTrigger>
              <TabsTrigger value="settings" className="rounded-none" data-testid="hr-tab-settings"><Settings2 className="w-3.5 h-3.5 mr-1.5" /> Settings</TabsTrigger>
            </TabsList>
            <Button size="sm" variant="outline"
                     className="rounded-none h-8"
                     onClick={() => { setCleanupOpen(true); setCleanupPreview(null); setCleanupResult(null); }}
                     title="Preview & soft-delete duplicate onboarding journeys per employee (admin only)."
                     data-testid="hr-cleanup-dupes">
              <Wand2 className="w-3.5 h-3.5 mr-1.5" />
              Fix duplicates
            </Button>
            <Button size="sm" variant="outline"
                     className="rounded-none h-8"
                     onClick={resyncJourneys}
                     disabled={resyncing}
                     title="Rehydrate every journey from the employee master and auto-start offboarding for anyone whose exit date was set without a journey."
                     data-testid="hr-resync-journeys">
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${resyncing ? "animate-spin" : ""}`} />
              {resyncing ? "Resyncing…" : "Resync HR journeys"}
            </Button>
          </div>
          <TabsContent value="onboarding" className="pt-4">
            <JourneyList rows={journeys} kind="onboarding" onOpen={(j) => setSelJourneyId(j.id)} />
          </TabsContent>
          <TabsContent value="buddy" className="pt-4">
            <BuddyProgramTab wsId={activeWorkspaceId} />
          </TabsContent>
          <TabsContent value="offboarding" className="pt-4">
            <JourneyList rows={offJourneys} kind="offboarding" onOpen={(j) => setSelJourneyId(`off:${j.id}`)} />
          </TabsContent>
          <TabsContent value="alumni" className="pt-4">
            <AlumniTab rows={alumni} onChange={loadAll} />
          </TabsContent>
          <TabsContent value="settings" className="pt-4">
            <SettingsTab wsId={activeWorkspaceId} />
          </TabsContent>
        </Tabs>
      </div>

      {selJourneyId && !selJourneyId.startsWith("off:") && (
        <JourneyDialog jid={selJourneyId} onClose={() => setSelJourneyId(null)} onChange={loadAll} />
      )}
      {selJourneyId && selJourneyId.startsWith("off:") && (
        <OffboardingDialog jid={selJourneyId.slice(4)} onClose={() => setSelJourneyId(null)} onChange={loadAll} />
      )}

      {/* R268iii · Feb 2026 — Admin-only cleanup wizard for duplicate journeys */}
      {cleanupOpen && (
        <Dialog open onOpenChange={(o) => !o && setCleanupOpen(false)}>
          <DialogContent className="rounded-none sm:max-w-lg" data-testid="hr-cleanup-dialog">
            <DialogHeader>
              <DialogTitle className="text-base flex items-center gap-2">
                <Wand2 className="w-4 h-4 text-amber-600" /> Fix duplicate onboarding journeys
              </DialogTitle>
              <DialogDescription className="text-[11px]">
                Finds employees with more than one non-completed journey (the "Nandita Deshmukh
                appears twice" bug) and soft-deletes the extras — keeping the earliest journey
                and moving any completed tasks onto it so no work is lost.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 text-xs">
              {!cleanupResult && !cleanupPreview && (
                <div className="flex flex-col gap-2 pt-1">
                  <div className="text-muted-foreground">
                    Step 1 — <span className="font-semibold text-foreground">Preview</span> the
                    counts without changing anything.
                  </div>
                  <label className="inline-flex items-center gap-2 text-[11px] cursor-pointer select-none"
                         data-testid="hr-cleanup-merge-toggle">
                    <input type="checkbox" checked={mergeByName}
                           onChange={(e) => { setMergeByName(e.target.checked); setCleanupPreview(null); }}
                           className="w-3 h-3" />
                    Also merge journeys with the same name across different employee records
                    <span className="text-amber-700 dark:text-amber-500 italic">(catches the "Nandita appears twice" case)</span>
                  </label>
                  <Button size="sm" variant="outline" className="rounded-none self-start"
                          data-testid="hr-cleanup-preview"
                          disabled={cleanupBusy}
                          onClick={async () => {
                            if (!activeWorkspaceId) return;
                            setCleanupBusy(true);
                            try {
                              const { data } = await api.post(
                                `/workspaces/${activeWorkspaceId}/onboarding/cleanup`,
                                { dry_run: true, merge_by_name: mergeByName });
                              setCleanupPreview(data);
                            } catch (e) { toast.error(apiErrorMessage(e, "Preview failed")); }
                            finally { setCleanupBusy(false); }
                          }}>
                    {cleanupBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                    Preview duplicates
                  </Button>
                </div>
              )}
              {cleanupPreview && !cleanupResult && (
                <div className="space-y-2 pt-1">
                  <div className="border border-border p-3 space-y-1 bg-muted/30">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Preview</div>
                    <div>Employees with duplicate journeys (same record): <span className="font-mono font-semibold">{cleanupPreview.duplicate_employees}</span></div>
                    {mergeByName && (
                      <div>Names shared across different records: <span className="font-mono font-semibold">{cleanupPreview.duplicate_names}</span></div>
                    )}
                    <div className="text-foreground font-semibold mt-1">Total journeys to soft-delete: <span className="font-mono">{cleanupPreview.journeys_marked_for_delete}</span></div>
                  </div>
                  {mergeByName && cleanupPreview.duplicate_names > 0 && (
                    <div className="border border-amber-300 bg-amber-50 dark:bg-amber-500/10 text-amber-900 dark:text-amber-200 p-2 text-[11px]">
                      Heads-up: name-merge collapses journeys whose employee names match. If two
                      genuinely different people share a name, they'd be merged too — audit the
                      list carefully before applying.
                    </div>
                  )}
                  {cleanupPreview.journeys_marked_for_delete === 0 ? (
                    <div className="text-emerald-700 font-medium">Nothing to clean — you're all good!</div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      <div className="text-muted-foreground">
                        Step 2 — Apply the cleanup. This soft-deletes {cleanupPreview.journeys_marked_for_delete} duplicate journey(s).
                      </div>
                      <Button size="sm" variant="outline"
                              className="rounded-none self-start border-rose-300 bg-rose-50 dark:bg-rose-500/10 text-rose-800 dark:text-rose-300 hover:bg-rose-100"
                              data-testid="hr-cleanup-apply"
                              disabled={cleanupBusy}
                              onClick={async () => {
                                if (!activeWorkspaceId) return;
                                setCleanupBusy(true);
                                try {
                                  const { data } = await api.post(
                                    `/workspaces/${activeWorkspaceId}/onboarding/cleanup`,
                                    { dry_run: false, merge_by_name: mergeByName });
                                  setCleanupResult(data);
                                  toast.success(`Cleaned ${data.journeys_marked_for_delete} duplicate journey(s)`);
                                  await loadAll();
                                } catch (e) { toast.error(apiErrorMessage(e, "Cleanup failed")); }
                                finally { setCleanupBusy(false); }
                              }}>
                        {cleanupBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                        Apply cleanup
                      </Button>
                    </div>
                  )}
                </div>
              )}
              {cleanupResult && (
                <div className="space-y-2 pt-1">
                  <div className="border border-emerald-300 p-3 space-y-1 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-900 dark:text-emerald-200">
                    <div className="font-semibold">Cleanup complete</div>
                    <div>Duplicate employees resolved: <span className="font-mono">{cleanupResult.duplicate_employees}</span></div>
                    <div>Journeys soft-deleted: <span className="font-mono font-semibold">{cleanupResult.journeys_marked_for_delete}</span></div>
                    <div className="text-[10px] opacity-70 mt-1">
                      The Onboarding list has been refreshed. New duplicates can no longer be
                      created — the create-journey path now checks for an existing non-completed
                      journey per employee.
                    </div>
                  </div>
                  <Button size="sm" variant="outline" className="rounded-none self-start"
                          data-testid="hr-cleanup-close"
                          onClick={() => setCleanupOpen(false)}>
                    Close
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function SummaryTiles({ s }) {
  if (!s) return <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mt-4">{Array.from({length: 6}).map((_,i)=>(<div key={i} className="border border-border h-20 animate-pulse bg-muted/30" />))}</div>;
  const tiles = [
    { label: "In flight", v: s.active, icon: Sparkles, tone: "text-primary", testid: "tile-active" },
    { label: "Pre-boarding", v: s.pre_boarding, icon: Rocket, testid: "tile-pre" },
    { label: "Joined", v: s.joined, icon: PartyPopper, tone: "text-emerald-600", testid: "tile-joined" },
    { label: "Overdue tasks", v: s.overdue_tasks, icon: AlertTriangle, tone: s.overdue_tasks > 0 ? "text-rose-600" : "", testid: "tile-overdue" },
    { label: "Offboarding", v: s.offboarding_in_progress, icon: LogOut, testid: "tile-offboarding" },
    { label: "Alumni", v: s.alumni_count, icon: GraduationCap, testid: "tile-alumni" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mt-4">
      {tiles.map((t) => (
        <Card key={t.label} className="rounded-none" data-testid={t.testid}>
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{t.label}</div>
              <t.icon className="w-3.5 h-3.5 text-muted-foreground" />
            </div>
            <div className={`text-2xl font-bold font-mono mt-1 ${t.tone || ""}`}>{t.v ?? 0}</div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function JourneyList({ rows, kind, onOpen }) {
  if (rows.length === 0) return <div className="border-2 border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid={`${kind}-empty`}>No {kind} journeys yet. Add an employee with &quot;Create Acquilix login&quot; checked to auto-start one.</div>;
  // Feb 2026 — Countdown to joining/exit date (positive = future, negative = past).
  const countdown = (d) => {
    if (!d) return null;
    const target = new Date(d + "T00:00:00Z");
    if (isNaN(target.getTime())) return null;
    const now = new Date();
    const ms = target.setUTCHours(0, 0, 0, 0) - Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
    return Math.round(ms / 86400000);
  };
  return (
    <JourneyTable rows={rows} kind={kind} onOpen={onOpen} countdown={countdown} />
  );
}

// Feb 2026 — Extracted so the sort state can be scoped without touching
// the tab layout above. Adds sortable column headers + a Designation
// and Role column requested by HR admins.
function JourneyTable({ rows, kind, onOpen, countdown }) {
  const [sortBy, setSortBy] = useState("date");
  const [sortDir, setSortDir] = useState("asc");
  const dateField = kind === "onboarding" ? "joining_date" : "exit_date";
  const sorted = useMemo(() => {
    const arr = [...rows];
    const dir = sortDir === "asc" ? 1 : -1;
    const key = (r) => {
      switch (sortBy) {
        case "employee": return (r.employee_name || "").toLowerCase();
        case "designation": return (r.designation || "").toLowerCase();
        case "role": return (r.employee_role || "").toLowerCase();
        case "status": return (r.status || "").toLowerCase();
        case "date": return r[dateField] || "";
        case "countdown": {
          const d = r[dateField];
          const n = countdown(d);
          return n == null ? Number.MAX_SAFE_INTEGER : n;
        }
        case "progress": return r.pct_complete || 0;
        default: return "";
      }
    };
    arr.sort((a, b) => {
      const va = key(a), vb = key(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
    return arr;
  }, [rows, sortBy, sortDir, dateField, countdown]);
  const setSort = (col) => {
    if (col === sortBy) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("asc"); }
  };
  const sortArrow = (col) => sortBy !== col
    ? <ArrowUpDown className="w-3 h-3 opacity-40 inline-block ml-1" />
    : (sortDir === "asc" ? <ArrowUp className="w-3 h-3 inline-block ml-1" /> : <ArrowDown className="w-3 h-3 inline-block ml-1" />);
  const H = ({ col, label, className = "" }) => (
    <button type="button" onClick={() => setSort(col)} className={`inline-flex items-center text-left hover:text-foreground ${className}`} data-testid={`${kind}-sort-${col}`}>
      {label}{sortArrow(col)}
    </button>
  );
  return (
    <div className="border border-border" data-testid={`${kind}-list`}>
      <div className="hidden md:grid grid-cols-14 gap-2 px-3 py-2 label-eyebrow text-[10px] bg-muted/40 border-b border-border" style={{ gridTemplateColumns: "minmax(0,3fr) minmax(0,2fr) minmax(0,1.5fr) minmax(0,1.5fr) minmax(0,1.5fr) 90px minmax(0,2fr) 40px" }}>
        <div><H col="employee" label="Employee" /></div>
        <div><H col="designation" label="Designation" /></div>
        <div><H col="role" label="Role" /></div>
        <div><H col="status" label="Status" /></div>
        <div><H col="date" label={kind === "onboarding" ? "Joining date" : "Exit date"} /></div>
        <div className="text-center"><H col="countdown" label="Countdown" className="justify-center" /></div>
        <div><H col="progress" label="Progress" /></div>
        <div className="text-right">Open</div>
      </div>
      {/* Mobile-only sort controls */}
      <div className="md:hidden flex items-center gap-1 px-3 py-2 border-b border-border bg-muted/40 overflow-x-auto">
        {[
          { k: "date", label: kind === "onboarding" ? "Join date" : "Exit date" },
          { k: "employee", label: "Name" },
          { k: "status", label: "Status" },
          { k: "progress", label: "Progress" },
        ].map((c) => (
          <button key={c.k} onClick={() => setSort(c.k)}
                   className={`text-[10px] uppercase tracking-widest px-2 py-1 border whitespace-nowrap ${sortBy === c.k ? "border-primary text-primary" : "border-border text-muted-foreground"}`}
                   data-testid={`${kind}-mobile-sort-${c.k}`}>
            {c.label}{sortBy === c.k ? (sortDir === "asc" ? " ↑" : " ↓") : ""}
          </button>
        ))}
      </div>
      {sorted.map((j) => {
        const date = j[dateField];
        const days = countdown(date);
        const pct = Math.round(j.pct_complete || 0);
        // R268yyy · Feb 2026 — Once HR has clicked "Confirm joined" (or
        // for offboarding, once the employee has actually exited), the
        // journey moves past `pre_boarding` / `notice`. In those cases,
        // don't keep incrementing the "N days overdue" counter — show a
        // static "Joined <date>" chip instead so the row stays calm.
        const isJoinedOrLater = kind === "onboarding"
          ? (j.status && j.status !== "pre_boarding")
          : (j.status === "completed" || j.actual_exit_date);
        const isOverdue = !isJoinedOrLater && days != null && days < 0 && j.status !== "completed";
        const isSoon = !isJoinedOrLater && days != null && days >= 0 && days <= 7;
        const chipTone = isJoinedOrLater
          ? "text-emerald-700 bg-emerald-500/10 border-emerald-500/30"
          : isOverdue
            ? "text-rose-700 bg-rose-500/10 border-rose-500/30"
            : isSoon
              ? "text-amber-700 bg-amber-500/10 border-amber-500/30"
              : days != null && days > 7
                ? "text-emerald-700 bg-emerald-500/10 border-emerald-500/30"
                : "text-muted-foreground bg-muted/40 border-border";
        const joinDateShort = (j.actual_join_date || j.joined_at || "").slice(0, 10);
        const label = isJoinedOrLater
          ? (kind === "onboarding"
              ? (joinDateShort ? `Joined ${joinDateShort}` : "Joined")
              : (j.status === "completed" ? "Exited" : "In progress"))
          : days == null
            ? "—"
            : days === 0
              ? "today"
              : days < 0
                ? `${Math.abs(days)}d overdue`
                : `in ${days}d`;
        const barTone = isOverdue && pct < 100
          ? "bg-rose-500"
          : (days != null && days > 30 && pct < 30)
            ? "bg-amber-500"
            : pct >= 100 ? "bg-emerald-500" : "bg-primary";
        return (
        <button key={j.id} onClick={() => onOpen(j)} className="w-full text-left border-b border-border last:border-0 hover:bg-muted/20" data-testid={`${kind}-row-${j.id}`}>
          {/* Desktop grid */}
          <div className="hidden md:grid gap-2 px-3 py-2 items-center" style={{ gridTemplateColumns: "minmax(0,3fr) minmax(0,2fr) minmax(0,1.5fr) minmax(0,1.5fr) minmax(0,1.5fr) 90px minmax(0,2fr) 40px" }}>
            <div className="font-semibold text-sm truncate" title={j.employee_name}>{j.employee_name || "—"}</div>
            <div className="text-xs truncate text-muted-foreground" title={j.designation || ""}>{j.designation || "—"}</div>
            <div className="text-xs truncate text-muted-foreground capitalize" title={j.employee_role || ""}>
              {j.employee_role ? j.employee_role.replace(/_/g, " ") : "—"}
            </div>
            <div>
              <span className={`chip text-[10px] border ${STATUS_TONES[j.status] || ""}`}>{j.status}</span>
            </div>
            <div className="text-xs font-mono">{date || "—"}</div>
            <div className="text-center">
              <span className={`chip text-[10px] border ${chipTone}`} data-testid={`${kind}-countdown-${j.id}`}>
                {label}
              </span>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-muted/60 rounded-sm overflow-hidden">
                  <div className={`h-full ${barTone}`} style={{ width: `${pct}%` }} data-testid={`${kind}-progress-bar-${j.id}`} />
                </div>
                <span className="text-[10px] font-mono tabular-nums" data-testid={`${kind}-progress-text-${j.id}`}>{pct}%</span>
              </div>
              <div className="text-[9px] text-muted-foreground font-mono mt-0.5">{j.task_done}/{j.task_total} tasks</div>
            </div>
            <div className="text-right"><ChevronRight className="w-4 h-4 inline-block text-muted-foreground" /></div>
          </div>
          {/* Mobile stacked layout */}
          <div className="md:hidden px-3 py-3 space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">{j.employee_name || "—"}</div>
                <div className="text-[11px] text-muted-foreground truncate">{j.designation || "—"}{j.employee_role ? ` · ${j.employee_role.replace(/_/g, " ")}` : ""}</div>
              </div>
              <ChevronRight className="w-4 h-4 flex-shrink-0 text-muted-foreground mt-0.5" />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`chip text-[10px] border ${STATUS_TONES[j.status] || ""}`}>{j.status}</span>
              <span className={`chip text-[10px] border ${chipTone}`} data-testid={`${kind}-countdown-mobile-${j.id}`}>{label}</span>
              {date && <span className="text-[10px] font-mono text-muted-foreground">{date}</span>}
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-1.5 bg-muted/60 rounded-sm overflow-hidden">
                <div className={`h-full ${barTone}`} style={{ width: `${pct}%` }} />
              </div>
              <span className="text-[10px] font-mono tabular-nums">{pct}%</span>
              <span className="text-[10px] text-muted-foreground font-mono">{j.task_done}/{j.task_total}</span>
            </div>
          </div>
        </button>
      );})}
    </div>
  );
}

function JourneyDialog({ jid, onClose, onChange }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [planPreview, setPlanPreview] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [promotePreview, setPromotePreviewState] = useState(null);
  const [members, setMembers] = useState([]);
  const [selection, setSelection] = useState([]);
  const [saveTplOpen, setSaveTplOpen] = useState(false);
  const [saveTplName, setSaveTplName] = useState("");
  const load = async () => {
    const { data } = await api.get(`/onboarding/journeys/${jid}`);
    setData(data);
    // Load workspace members once (for reviewer / approver pickers on tasks).
    if (data?.journey?.workspace_id && members.length === 0) {
      try {
        const r = await api.get(`/workspaces/${data.journey.workspace_id}/members`);
        setMembers(r.data || []);
      } catch { /* member list is optional — pickers still show ids */ }
    }
  };
  useEffect(() => { load(); }, [jid]);

  const openPromote = async () => {
    try {
      const r = await api.get(`/onboarding/journeys/${jid}/promotion-preview`);
      setPromotePreviewState(r.data);
      setPromoteOpen(true);
    } catch (e) { toast.error(apiErrorMessage(e, "Preview failed")); }
  };
  const doPromote = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/onboarding/journeys/${jid}/promote-to-goals`);
      if (r.data.already_promoted) {
        toast.info("Already promoted — opening the parent OKR.");
      } else {
        toast.success(`🎯 Promoted ${r.data.kra_goals.length} KRAs + ${r.data.kpi_count} KPIs to Goals.`);
      }
      setPromoteOpen(false);
      await load();
      // Navigate to the Goal
      window.open(`/app/goals?open=${r.data.parent_goal_id}`, "_blank", "noopener");
    } catch (e) { toast.error(apiErrorMessage(e, "Promote failed")); }
    finally { setBusy(false); }
  };

  // Task-row mutations now flow through OnboardingTaskItem which hits the
  // API directly and reloads via `onChanged` — no local patchTask helper
  // needed on this component.
  const sendWelcome = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/onboarding/journeys/${jid}/welcome-email`);
      toast.success(r.data.sent ? `Welcome email sent to ${r.data.to}` : "Email queued (delivery pending)");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Send failed")); }
    finally { setBusy(false); }
  };
  const draftPlan = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/onboarding/journeys/${jid}/draft-success-plan`);
      toast.success(r.data.ai_used ? "AI drafted 30/60/90 plan — review before sending" : "Draft ready (stub)");
      await load();
      setPlanPreview(true);
    } catch (e) { toast.error(apiErrorMessage(e, "Draft failed")); }
    finally { setBusy(false); }
  };
  const sendPlan = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/onboarding/journeys/${jid}/send-success-plan`);
      toast.success(r.data.sent ? `Plan sent to ${r.data.to}` : "Plan queued");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Send failed")); }
    finally { setBusy(false); }
  };
  const savePlan = async (planMd, kraMd) => {
    try {
      await api.patch(`/onboarding/journeys/${jid}/success-plan`, { success_plan_md: planMd, kra_kpi_md: kraMd });
      toast.success("Plan saved");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
  };
  const confirmJoined = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/onboarding/journeys/${jid}/confirm-joined`);
      if (r.data?.trigger_celebration) fireConfetti();
      toast.success(`🎉 Welcome to ${r.data.workspace_name || "the team"}!`);
      await onChange?.();
      setConfirmOpen(false);
      onClose();
    } catch (e) { toast.error(apiErrorMessage(e, "Confirm failed")); }
    finally { setBusy(false); }
  };
  const saveAsTemplate = async () => {
    if (!saveTplName.trim()) return;
    setBusy(true);
    try {
      const r = await api.post(`/onboarding/journeys/${jid}/save-as-template`,
        { name: saveTplName.trim(), scope: "workspace" });
      toast.success(`Template saved · ${(r.data.tasks || []).length} tasks`);
      setSaveTplOpen(false);
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  if (!data) return null;
  const j = data.journey;
  const tasksByCat = groupBy(data.tasks, "category");

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="w-[95vw] sm:!max-w-[92vw] max-h-[92vh] overflow-auto" data-testid="journey-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Rocket className="w-4 h-4" />
            {j.employee_name}
            <span className={`chip text-[10px] border ${STATUS_TONES[j.status] || ""}`}>{j.status}</span>
          </DialogTitle>
          <DialogDescription>Full onboarding journey — send welcome, draft the 30/60/90 plan, and mark tasks complete.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {/* Hero action bar */}
          <div className="border-2 border-primary/30 bg-gradient-to-r from-primary/5 to-transparent p-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Quick actions</div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" className="rounded-none h-8" onClick={sendWelcome} disabled={busy} data-testid="j-send-welcome">
                <Mail className="w-3.5 h-3.5 mr-1" /> {j.welcome_sent_at ? "Resend welcome email" : "Send welcome email"}
              </Button>
              <Button size="sm" variant="outline" className="rounded-none h-8" onClick={draftPlan} disabled={busy} data-testid="j-draft-plan">
                <Sparkles className="w-3.5 h-3.5 mr-1" /> {j.success_plan_md ? "Re-draft AI 30/60/90 plan" : "AI draft 30/60/90 plan"}
              </Button>
              {j.success_plan_md && (
                <Button size="sm" variant="outline" className="rounded-none h-8" onClick={() => setPlanPreview(true)} data-testid="j-view-plan">
                  <ExternalLink className="w-3.5 h-3.5 mr-1" /> View / edit plan
                </Button>
              )}
              {j.success_plan_md && (
                <Button size="sm" variant="outline" className="rounded-none h-8" onClick={sendPlan} disabled={busy} data-testid="j-send-plan">
                  <Mail className="w-3.5 h-3.5 mr-1" /> {j.success_plan_sent_at ? "Resend plan" : "Send plan to employee"}
                </Button>
              )}
              {(j.success_plan_md || j.kra_kpi_md) && (
                <Button size="sm" variant="outline" className="rounded-none h-8" onClick={openPromote} disabled={busy} data-testid="j-promote-goals">
                  <Target className="w-3.5 h-3.5 mr-1" /> {j.promoted_goal_id ? "Open promoted goal" : "Promote to Goals & OKRs"}
                </Button>
              )}
              {!j.joined_confirmed_at && (
                <Button size="sm" className="rounded-none h-8 bg-emerald-600 hover:bg-emerald-700 text-white ml-auto" onClick={() => setConfirmOpen(true)} disabled={busy} data-testid="j-confirm-joined">
                  <PartyPopper className="w-3.5 h-3.5 mr-1" /> Confirm joined
                </Button>
              )}
              <Button size="sm" variant="outline" className="rounded-none h-8" onClick={() => { setSaveTplName(`${j.employee_name || "Journey"} template`); setSaveTplOpen(true); }} data-testid="j-save-template">
                <Sparkles className="w-3.5 h-3.5 mr-1" /> Save as template
              </Button>
            </div>
            <div className="text-[10px] text-muted-foreground mt-2 flex gap-3 flex-wrap">
              <span>Joining: <b>{j.joining_date || "—"}</b></span>
              {j.welcome_sent_at && <span className="text-emerald-700">✓ welcome sent {j.welcome_sent_at.slice(0, 10)}</span>}
              {j.success_plan_md && <span className="text-emerald-700">✓ plan drafted</span>}
              {j.success_plan_sent_at && <span className="text-emerald-700">✓ plan sent {j.success_plan_sent_at.slice(0, 10)}</span>}
              {j.joined_confirmed_at && <span className="text-emerald-700">✓ joined {j.joined_confirmed_at.slice(0, 10)}</span>}
              {j.promoted_goal_id && <span className="text-emerald-700">✓ promoted to Goals</span>}
            </div>
          </div>

          {/* Tasks by category */}
          {Object.entries(tasksByCat).map(([cat, list]) => (
            <div key={cat} className="border border-border">
              <div className="px-3 py-2 border-b border-border bg-muted/40 label-eyebrow text-[10px] flex items-center justify-between">
                <span>{cat.replace(/_/g, " ")} · {list.length} tasks</span>
                <div className="flex items-center gap-3">
                  <button
                    className="text-[10px] text-muted-foreground hover:text-primary"
                    onClick={() => {
                      const ids = list.map((t) => t.id);
                      const allSel = ids.every((id) => selection.includes(id));
                      setSelection((s) => allSel
                        ? s.filter((id) => !ids.includes(id))
                        : Array.from(new Set([...s, ...ids])));
                    }}
                    data-testid={`sel-cat-${cat}`}
                  >
                    {list.every((t) => selection.includes(t.id)) ? "Unselect all" : "Select all"}
                  </button>
                  <AddCustomTaskButton
                    jid={jid}
                    kind="onboarding"
                    category={cat}
                    members={members}
                    onCreated={async () => { await load(); await onChange?.(); }}
                  />
                  <BulkAddTasksButton
                    jid={jid}
                    kind="onboarding"
                    category={cat}
                    members={members}
                    onCreated={async () => { await load(); await onChange?.(); }}
                  />
                  <ImportFromTemplateButton
                    jid={jid}
                    wsId={j?.workspace_id}
                    kind="onboarding"
                    category={cat}
                    onCreated={async () => { await load(); await onChange?.(); }}
                  />
                </div>
              </div>
              <TaskListHeader selectable />
              <SortableTaskList
                tasks={list}
                kind="onboarding"
                members={members}
                selection={selection}
                onToggleSelect={(id) => setSelection((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id])}
                onChanged={async () => { await load(); await onChange?.(); }}
                onReordered={async () => { await load(); }}
              />
            </div>
          ))}

          <BulkTaskToolbar
            selection={selection}
            kind="onboarding"
            members={members}
            onDone={async () => { setSelection([]); await load(); await onChange?.(); }}
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose} data-testid="j-close">Close</Button>
        </DialogFooter>
      </DialogContent>
      {planPreview && (
        <PlanEditor journey={j} onClose={() => setPlanPreview(false)} onSave={savePlan} />
      )}
      <Dialog open={saveTplOpen} onOpenChange={setSaveTplOpen}>
        <DialogContent className="z-[130] max-w-md" data-testid="save-tpl-dialog">
          <DialogHeader>
            <DialogTitle>Save this journey as a template</DialogTitle>
            <DialogDescription>Snapshot every task on this checklist (title, description, owner, category, is-required, visibility) into a reusable workspace template. Per-employee fields like due dates and reviewer/approver assignments are skipped.</DialogDescription>
          </DialogHeader>
          <Input value={saveTplName} onChange={(e) => setSaveTplName(e.target.value)} className="rounded-none h-9" placeholder="Template name" data-testid="save-tpl-name" maxLength={120} />
          <DialogFooter>
            <Button variant="ghost" className="rounded-none" onClick={() => setSaveTplOpen(false)}>Cancel</Button>
            <Button className="rounded-none" onClick={saveAsTemplate} disabled={busy || !saveTplName.trim()} data-testid="save-tpl-save">Save template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="z-[130]" data-testid="confirm-joined-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm the employee has joined?</AlertDialogTitle>
            <AlertDialogDescription>
              This triggers the welcome celebration and auto-posts a pinned welcome bulletin to the workspace.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="confirm-joined-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction className="rounded-none bg-emerald-600 hover:bg-emerald-700" onClick={confirmJoined} disabled={busy} data-testid="confirm-joined-ok">
              Yes, celebrate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={promoteOpen} onOpenChange={setPromoteOpen}>
        <AlertDialogContent className="z-[130]" data-testid="promote-goals-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Target className="w-4 h-4" /> Promote 30/60/90 plan to Goals &amp; OKRs?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>Creates one <b>OKR goal</b> with Day 30/60/90 milestones, and <b>one KRA goal per row</b> with KPIs as key-results — owned by the new hire.</p>
                {promotePreview && (
                  <div className="border border-border bg-muted/30 p-3 text-xs" data-testid="promote-preview">
                    <div className="grid grid-cols-3 gap-2 mb-2">
                      <div><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Phases detected</div><div className="text-lg font-bold">{promotePreview.phase_count || 0}<span className="text-[10px] text-muted-foreground font-normal ml-1">/ 3 milestones</span></div></div>
                      <div><div className="text-[10px] uppercase tracking-widest text-muted-foreground">KRAs</div><div className="text-lg font-bold">{promotePreview.kra_count}</div></div>
                      <div><div className="text-[10px] uppercase tracking-widest text-muted-foreground">KPIs</div><div className="text-lg font-bold">{promotePreview.kpi_count}</div></div>
                    </div>
                    {promotePreview.kras && promotePreview.kras.length > 0 && (
                      <ul className="text-[11px] space-y-0.5 max-h-40 overflow-auto">
                        {promotePreview.kras.map((k, i) => (
                          <li key={i}><b className="text-foreground">{k.title}</b> <span className="text-muted-foreground">— {k.kpis.length} KPIs</span></li>
                        ))}
                      </ul>
                    )}
                    {promotePreview.already_promoted && (
                      <div className="mt-2 text-amber-600 text-[11px]">⚠ Already promoted. Clicking will open the existing goal.</div>
                    )}
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="promote-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction className="rounded-none" onClick={doPromote} disabled={busy || (!promotePreview?.has_plan && !promotePreview?.has_kra)} data-testid="promote-ok">
              {promotePreview?.already_promoted ? "Open goal" : "Promote 🎯"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </Dialog>
  );
}

function PlanEditor({ journey, onClose, onSave }) {
  const [plan, setPlan] = useState(journey.success_plan_md || "");
  const [kra, setKra] = useState(journey.kra_kpi_md || "");
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-auto" data-testid="plan-editor">
        <DialogHeader>
          <DialogTitle>30/60/90 Day Plan + KRA/KPI</DialogTitle>
          <DialogDescription>Review and edit the AI-drafted plan before sending it to the new hire.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="label-eyebrow text-[10px] mb-1 block">Success plan (Markdown)</Label>
            <Textarea value={plan} onChange={(e) => setPlan(e.target.value)} className="min-h-[240px] font-mono text-xs rounded-none" data-testid="plan-text" />
          </div>
          <div>
            <Label className="label-eyebrow text-[10px] mb-1 block">KRA / KPI (Markdown)</Label>
            <Textarea value={kra} onChange={(e) => setKra(e.target.value)} className="min-h-[160px] font-mono text-xs rounded-none" data-testid="kra-text" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={async () => { await onSave(plan, kra); onClose(); }} data-testid="plan-save">Save plan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function AlumniTab({ rows, onChange }) {
  const togglePortal = async (a) => {
    try {
      await api.patch(`/alumni/${a.id}`, { portal_access: !a.portal_access });
      toast.success(!a.portal_access ? "Alumni portal enabled" : "Alumni portal disabled");
      await onChange?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
  };
  if (rows.length === 0) return <div className="border-2 border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="alumni-empty">No alumni yet. Offboarded employees will land here.</div>;
  return (
    <div className="border border-border" data-testid="alumni-list">
      <div className="grid grid-cols-12 gap-2 px-3 py-2 label-eyebrow text-[10px] bg-muted/40 border-b border-border">
        <div className="col-span-4">Name</div>
        <div className="col-span-3">Work email</div>
        <div className="col-span-2">Exit date</div>
        <div className="col-span-3 text-right">Portal access</div>
      </div>
      {rows.map((a) => (
        <div key={a.id} className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-border last:border-0 items-center" data-testid={`alumni-row-${a.id}`}>
          <div className="col-span-4 font-semibold text-sm">{a.full_name || "—"}</div>
          <div className="col-span-3 text-xs font-mono truncate">{a.work_email || "—"}</div>
          <div className="col-span-2 text-xs font-mono">{a.exit_date || "—"}</div>
          <div className="col-span-3 text-right">
            <button onClick={() => togglePortal(a)} className={`chip text-[10px] border ${a.portal_access ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-700" : "bg-muted border-border text-muted-foreground"}`} data-testid={`alumni-toggle-${a.id}`}>
              {a.portal_access ? "Enabled" : "Disabled"}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function SettingsTab({ wsId }) {
  const [settings, setSettings] = useState(null);
  const [offSettings, setOffSettings] = useState(null);
  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/alumni/settings`).then((r) => setSettings(r.data)).catch(() => setSettings({ enabled: true }));
    api.get(`/workspaces/${wsId}/offboarding/settings`).then((r) => setOffSettings(r.data)).catch(() => setOffSettings({ goodbye_email_enabled: false }));
  }, [wsId]);
  const save = async (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    try {
      await api.patch(`/workspaces/${wsId}/alumni/settings`, next);
      toast.success("Settings updated");
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
  };
  const saveOff = async (patch) => {
    const next = { ...offSettings, ...patch };
    setOffSettings(next);
    try {
      await api.patch(`/workspaces/${wsId}/offboarding/settings`, next);
      toast.success("Offboarding settings updated");
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
  };
  if (!settings || !offSettings) return null;
  return (
    <div className="space-y-4" data-testid="settings-tab">
      {/* Alumni portal */}
      <div className="border border-border p-4 space-y-3">
        <div className="label-eyebrow text-[10px]">Alumni portal</div>
        <p className="text-[11px] text-muted-foreground">Control whether offboarded employees can access their alumni portal (letters, payslips, alumni feed).</p>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={!!settings.enabled} onChange={(e) => save({ enabled: e.target.checked })} data-testid="settings-alumni-enabled" />
          <span>Enable Alumni portal for this workspace</span>
        </label>
        {settings.enabled && (
          <>
            <label className="flex items-center gap-2 text-sm cursor-pointer ml-6">
              <input type="checkbox" checked={settings.allow_letter_download !== false} onChange={(e) => save({ allow_letter_download: e.target.checked })} data-testid="settings-alumni-letters" />
              <span>Allow relieving / experience letter download</span>
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer ml-6">
              <input type="checkbox" checked={settings.allow_payslip_download !== false} onChange={(e) => save({ allow_payslip_download: e.target.checked })} data-testid="settings-alumni-payslips" />
              <span>Allow payslip download</span>
            </label>
          </>
        )}
        <p className="text-[10px] text-muted-foreground pt-2 border-t border-border">HR can individually toggle portal access per alumnus from the Alumni tab.</p>
      </div>

      {/* Goodbye email */}
      <div className="border border-border p-4 space-y-3">
        <div className="label-eyebrow text-[10px]">Goodbye email 💌</div>
        <p className="text-[11px] text-muted-foreground">
          When enabled, HR gets an AI-drafted goodbye email option on every offboarding — reviewed and sent manually. Great for keeping departing employees as friends &amp; potential rehires.
        </p>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={!!offSettings.goodbye_email_enabled}
                 onChange={(e) => saveOff({ goodbye_email_enabled: e.target.checked })} data-testid="settings-goodbye-enabled" />
          <span>Enable AI-drafted goodbye email</span>
        </label>
        {offSettings.goodbye_email_enabled && (
          <div className="ml-6 space-y-2">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Custom AI prompt (optional)</div>
              <Textarea rows={4} placeholder="Leave blank to use the default warm-and-brand-safe prompt."
                        value={offSettings.goodbye_email_prompt || ""}
                        onChange={(e) => setOffSettings({ ...offSettings, goodbye_email_prompt: e.target.value })}
                        onBlur={() => saveOff({ goodbye_email_prompt: offSettings.goodbye_email_prompt || null })}
                        data-testid="settings-goodbye-prompt" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Signature (optional)</div>
              <Input placeholder="— The Acme team" value={offSettings.goodbye_email_signature || ""}
                     onChange={(e) => setOffSettings({ ...offSettings, goodbye_email_signature: e.target.value })}
                     onBlur={() => saveOff({ goodbye_email_signature: offSettings.goodbye_email_signature || null })}
                     data-testid="settings-goodbye-signature" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function groupBy(arr, k) {
  const out = {};
  (arr || []).forEach((a) => {
    const key = a[k] || "other";
    out[key] = out[key] || [];
    out[key].push(a);
  });
  return out;
}

function fireConfetti() {
  const duration = 3000;
  const end = Date.now() + duration;
  (function frame() {
    confetti({ particleCount: 5, angle: 60, spread: 55, origin: { x: 0 }, colors: ["#0ea5e9", "#22c55e", "#f59e0b", "#ec4899"] });
    confetti({ particleCount: 5, angle: 120, spread: 55, origin: { x: 1 }, colors: ["#0ea5e9", "#22c55e", "#f59e0b", "#ec4899"] });
    if (Date.now() < end) requestAnimationFrame(frame);
  }());
  // Center burst
  confetti({ particleCount: 120, spread: 90, origin: { y: 0.6 } });
}

function CountdownStrip({ rows, onOpen, onLink }) {
  return (
    <div className="border-2 border-primary/30 bg-primary/5 mt-4 p-3" data-testid="countdown-strip">
      <div className="label-eyebrow text-[10px] mb-2 flex items-center gap-1.5">
        <Rocket className="w-3 h-3 text-primary" /> Upcoming joinings ({rows.length})
      </div>
      <div className="flex gap-3 overflow-x-auto pb-1">
        {rows.map((r) => {
          const days = r.days_to_joining;
          const tone = days == null ? "text-muted-foreground" : days <= 3 ? "text-rose-600" : days <= 14 ? "text-amber-600" : "text-primary";
          return (
            <div key={r.id} className="border border-border bg-background min-w-[180px] p-3 flex-shrink-0" data-testid={`countdown-${r.id}`}>
              <div className="text-sm font-semibold truncate">{r.employee_name}</div>
              <div className="text-[10px] text-muted-foreground mt-0.5">{r.joining_date || "—"}</div>
              <div className={`text-2xl font-bold font-mono mt-1 ${tone}`} data-testid={`countdown-days-${r.id}`}>
                {days == null ? "—" : days < 0 ? `${Math.abs(days)}d late` : days === 0 ? "TODAY!" : `${days}d`}
              </div>
              <div className="flex gap-1 mt-2">
                <button className="text-[10px] text-primary hover:underline" onClick={() => onOpen(r)} data-testid={`countdown-open-${r.id}`}>Open</button>
                <button className="text-[10px] text-primary hover:underline ml-auto" onClick={() => onLink(r.id)} data-testid={`countdown-magic-${r.id}`}>Preview portal ↗</button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PendingCheckinBanner({ checkin, onDone }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div className="border-2 border-emerald-500/40 bg-emerald-500/5 mt-4 p-3 flex items-center gap-3" data-testid="my-checkin-banner">
        <Sparkles className="w-4 h-4 text-emerald-600" />
        <div className="flex-1">
          <div className="text-sm font-semibold">Your weekly check-in is ready · Week of {checkin.week_of}</div>
          <div className="text-[11px] text-muted-foreground">Two minutes to help us make your onboarding even better.</div>
        </div>
        <Button size="sm" className="rounded-none h-8 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setOpen(true)} data-testid="my-checkin-open">
          Take check-in
        </Button>
      </div>
      {open && <CheckinDialog checkin={checkin} onClose={() => setOpen(false)} onDone={async () => { setOpen(false); await onDone?.(); }} />}
    </>
  );
}

function CheckinDialog({ checkin, onClose, onDone }) {
  const [r, setR] = useState({ energy: 3, clarity: 3, connection: 3, wins: "", blockers: "", ask: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/onboarding/checkins/${checkin.id}/submit`, { responses: r });
      toast.success(`Thanks! Sentiment: ${res.data.sentiment} (${res.data.sentiment_score})`);
      await onDone?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Submit failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="checkin-dialog">
        <DialogHeader>
          <DialogTitle>Weekly check-in · Week of {checkin.week_of}</DialogTitle>
          <DialogDescription>Two minutes — your honest answers help HR & your manager support you.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {["energy", "clarity", "connection"].map((k) => {
            const q = checkin.questions.find((x) => x.key === k);
            return (
              <div key={k}>
                <Label className="text-sm font-semibold">{q.label}</Label>
                <div className="text-[10px] text-muted-foreground mb-1.5">{q.help}</div>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} type="button" onClick={() => setR({ ...r, [k]: n })} className={`w-10 h-10 border-2 font-mono text-sm ${r[k] === n ? "bg-primary border-primary text-primary-foreground" : "border-border hover:border-primary"}`} data-testid={`checkin-${k}-${n}`}>{n}</button>
                  ))}
                </div>
              </div>
            );
          })}
          {["wins", "blockers", "ask"].map((k) => {
            const q = checkin.questions.find((x) => x.key === k);
            return (
              <div key={k}>
                <Label className="text-sm font-semibold">{q.label}</Label>
                <Textarea value={r[k]} onChange={(e) => setR({ ...r, [k]: e.target.value })} className="min-h-[60px] rounded-none text-sm" data-testid={`checkin-${k}`} />
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy} data-testid="checkin-submit">
            {busy ? "Submitting…" : "Submit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


